import { queryOptions } from "@tanstack/react-query";
import { isRestaurantCuisine } from "@majourneys/shared";
import { activitiesApi } from "./activities.api";
import { activityKeys, type ActivityFilters } from "../constants/query-keys";
import { isExploreSegmentSlug, isUnionHubSlug } from "../lib/explore-segments";
import { hubWhenFilters } from "../lib/hub-when";
import { hasCollections, isHubCollection } from "../lib/hub-collections";

export interface ActivityRouteParams {
  lang?: string;
  category?: string;
  city?: string;
}

export interface ActivityRouteSearch {
  q?: string;
  /* Written as numbers, but a link from before that — or a hand-typed one —
     still arrives as a string. Both are read. */
  page?: string | number;
  min_price?: string | number;
  max_price?: string | number;
  sort?: string;
  /** Union-hub audience filter: unset (all), "bookable", or "explore". */
  show?: string;
  /** Restaurants or spas hub chip (an id from hubCollections). */
  tag?: string;
  /** Restaurants hub cuisine chip (an id from RESTAURANT_CUISINE_IDS). */
  cuisine?: string;
  /** The booking bar's day, YYYY-MM-DD (hubs with opening hours). */
  date?: string;
  /** The booking bar's time, HH:mm. */
  time?: string;
  /** "Open now" chip. */
  open?: string | number | boolean;
  /** "Tables tonight" chip. */
  tonight?: string | number | boolean;
}

/** A price bound as the API wants it: a plain string, or absent. Keeps the
 *  React Query key stable whether the URL carried a number or a string. */
function numeric(raw: string | number | undefined): string | undefined {
  if (raw === undefined || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : undefined;
}

/**
 * What a union hub lists. Unset is the hub's default: bookable-only on the
 * BOOKABLE_FIRST_HUBS, both halves elsewhere. "all" asks for both explicitly.
 */
export type ShowFilter = "bookable" | "explore" | "all";

/**
 * Hubs that list only what can be booked unless the guest picks "All" in
 * Filters (owner, 2026-10-07). The API's `bookable_first` falls back to every
 * row in a city with nothing bookable, so these never go blank.
 */
export const BOOKABLE_FIRST_HUBS: ReadonlySet<string> = new Set(["restaurants", "spas"]);

/**
 * Read the `show` search param.
 *
 * Strips surrounding quotes because the router's default search serialization
 * JSON-encodes values on client navigation, so a chip click lands as
 * `?show=%22bookable%22` while a crawler or a pasted link arrives as
 * `?show=bookable`. Both have to parse to the same filter, or the control
 * appears to do nothing when clicked.
 */
export function parseShowFilter(raw: unknown): ShowFilter | undefined {
  const value = typeof raw === "string" ? raw.replace(/^"+|"+$/g, "") : undefined;
  return value === "bookable" || value === "explore" || value === "all" ? value : undefined;
}

/**
 * Single source of truth for the explore-grid filter object.
 *
 * Both the route `loader` (which prefetches via `ensureQueryData`) and the
 * `ExplorePage` component (which reads via `useSuspenseQuery`) build their
 * filters through this helper, so the React Query keys match **exactly**. If
 * they diverged, the loader would warm one cache key and the component would
 * suspend on a different one — re-triggering the full-page spinner this whole
 * setup exists to avoid.
 */
export function buildActivityFilters(
  params: ActivityRouteParams,
  search: ActivityRouteSearch,
): ActivityFilters {
  const q = search.q?.trim() || undefined;
  const isCityOverview = !!params.city && !params.category && !q;
  // `/category/restaurants/marrakech` and friends are segment hubs, not
  // category hubs. Filtering them by `category` returns nothing — "restaurants"
  // is not in the categories table — so the page rendered its fallback grid and
  // set `noindex,follow` on itself, despite having a correct title, canonical
  // and 93 matching venues one filter away. `segment` needs `mode=explore`:
  // the default mode excludes explore rows entirely.
  const segment =
    params.category && isExploreSegmentSlug(params.category)
      ? params.category
      : undefined;
  // `spas` and `stays` name a bookable category AND an explore segment, and
  // neither filter alone returns both halves. `hub=` unions them server-side
  // and defaults to mode=all, so the hub stops dropping half its own inventory.
  const hub =
    params.category && isUnionHubSlug(params.category) ? params.category : undefined;
  // The audience filter only exists on a union hub — it is the only place with
  // both halves to choose between. Elsewhere it is ignored rather than allowed
  // to empty the grid (a `show=bookable` on a restaurants hub would match
  // nothing at all).
  // Stays lists only what can be booked (owner, 2026-09-26): its camps from
  // this catalogue, with every LiteAPI property beside them (StaysHubGrid).
  // The explore-only hotel guides left the list.
  const parsedShow = hub ? parseShowFilter(search.show) : undefined;
  const show =
    hub === "stays"
      ? "bookable"
      : parsedShow === "all"
        ? undefined // the API's hub default is both halves
        : parsedShow ?? (hub && BOOKABLE_FIRST_HUBS.has(hub) ? "bookable_first" : undefined);
  // The chip rows (Dinner shows, Rooftops… on restaurants; Hammam, Couples…
  // on spas). The tag is filtered on the server so the total and Show more
  // stay right; an unknown tag in a hand-typed URL is dropped, not sent.
  const collections =
    hub && hasCollections(hub)
      ? {
          tag: isHubCollection(hub, search.tag) ? search.tag : undefined,
          cuisine: hub === "restaurants" && isRestaurantCuisine(search.cuisine) ? search.cuisine : undefined,
          tag_counts: "true",
        }
      : {};
  const noPriceHub = params.category === "restaurants";
  return {
    q,
    ...(segment
      ? { segment, mode: "explore" as const }
      : hub
        ? { hub, ...(show ? { mode: show } : {}) }
        : { category: params.category }),
    city: params.city,
    // Restaurants have no price, so the hub has no price filter or price sort
    // (owner, 2026-10-01): one left in an old link must not narrow the list
    // out of sight.
    min_price: noPriceHub ? undefined : numeric(search.min_price),
    max_price: noPriceHub ? undefined : numeric(search.max_price),
    sort: noPriceHub && search.sort?.startsWith("price_") ? undefined : search.sort,
    page: String(Number(search.page) || 1),
    page_size: isCityOverview ? "200" : "12",
    lang: params.lang ?? "en",
    ...collections,
    // The booking bar's day and time and the "Open now" / "Tables tonight"
    // chips, on the hubs whose venues carry opening hours. Filtered on the
    // server so the total and Show more stay right.
    ...hubWhenFilters(params.category, search as Record<string, unknown>),
    // A plain submitted search, not narrowed to a category: if it finds
    // nothing, the API counts it as demand we do not meet (admin Supply and
    // demand). Page 1 only, so paging never counts twice.
    ...(q && !params.category && (Number(search.page) || 1) === 1 ? { log: "1" } : {}),
  };
}

/**
 * One row of a city hub's crawlable explore index — just enough to build a
 * canonical href and print a title.
 */
export interface CityExploreLinkRow {
  id: string;
  slug: string;
  title: string;
  segment: string | null;
  categorySlug: string | null;
  citySlug: string | null;
}

interface RawExploreRow {
  id: string;
  slug: string;
  title: string;
  segment?: string | null;
  category?: { slug?: string | null } | null;
  city?: { slug?: string | null } | null;
}

/**
 * Explore-mode rows for the city hub's crawlable link index.
 *
 * `mode=explore` is the point: the hub's main grid is bookable-only in
 * practice, so `/activities/explore/search?city=marrakech` returns 36 rows
 * without it and 164 with it. Those extra rows are the restaurants,
 * attractions, spas and beach clubs that had no server-rendered link anywhere
 * on the site. `/discover` browses them but renders client-side, so a crawler
 * saw 3 links in 43 KB of HTML and could reach the pages only via the sitemap.
 *
 * The response is projected down to `CityExploreLinkRow` inside `queryFn`,
 * not in a `select`. React Query dehydrates what the *query function*
 * returned, so a `select` would still ship every full row into the SSR HTML:
 * measured at 59 KB of `overview` prose on the Marrakech hub, serialised so a
 * list of titles could render. Narrowing here keeps that out of the payload.
 *
 * Its own key (`activityKeys.exploreLinks`) rather than a `list` key, so the
 * slim rows can never be served to the grid, which needs the full shape.
 */
export function cityExploreLinksQueryOptions(city: string, lang?: string) {
  const filters: ActivityFilters = {
    city,
    mode: "explore",
    page: "1",
    page_size: "200",
    lang: lang ?? "en",
  };
  return queryOptions({
    queryKey: activityKeys.exploreLinks(city, lang),
    queryFn: async (): Promise<CityExploreLinkRow[]> => {
      const response = await activitiesApi.list(filters);
      const rows = (response.data ?? []) as RawExploreRow[];
      return rows.map((row) => ({
        id: row.id,
        slug: row.slug,
        title: row.title,
        segment: row.segment ?? null,
        categorySlug: row.category?.slug ?? null,
        citySlug: row.city?.slug ?? null,
      }));
    },
  });
}

export function activitiesQueryOptions(filters: ActivityFilters) {
  return queryOptions({
    queryKey: activityKeys.list(filters),
    queryFn: () => activitiesApi.list(filters),
  });
}

/** One row of `/cities/explore`, as far as the empty-hub rail needs it. */
export interface CityListRow {
  slug: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  /** Published bookable activities, transfers excluded. */
  activitiesCount: number;
}

/**
 * The city list with bookable counts and coordinates, for the empty-hub rail.
 *
 * Its own key rather than `useCities`' one because a failure here resolves to
 * an empty list: the rail is a nice-to-have on a page that is already empty,
 * and must never take the hub down with it or cache an empty list for pages
 * that do need the cities.
 */
export function emptyHubCitiesQueryOptions(lang: string) {
  return queryOptions({
    queryKey: [...activityKeys.cities(), lang, "empty-hub"],
    queryFn: async (): Promise<CityListRow[]> => {
      try {
        const response = await activitiesApi.listCities(lang);
        const rows = (response?.data ?? []) as Array<Partial<CityListRow>>;
        return rows
          .filter((row): row is Partial<CityListRow> & { slug: string } => !!row.slug)
          .map((row) => ({
            slug: row.slug,
            name: row.name ?? row.slug,
            latitude: typeof row.latitude === "number" ? row.latitude : null,
            longitude: typeof row.longitude === "number" ? row.longitude : null,
            activitiesCount: Number(row.activitiesCount) || 0,
          }));
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** One card of the empty-hub rail: exactly what `ActivityCardDiscover` takes. */
export interface NearbyRailItem {
  isNew?: boolean;
  id: string;
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number;
  mode?: string;
  segment: string | null;
  city: { name: string; slug: string };
  category: { name: string; slug: string } | null;
}

/**
 * What the "book nearby" rail on an empty hub reads from one city: bookable
 * rows only (explore rows and transfers are excluded server-side), mixed by
 * category so one city's camel rides cannot fill the whole rail.
 */
export function nearbyBookableFilters(citySlug: string, lang: string): ActivityFilters {
  return {
    city: citySlug,
    mode: "bookable",
    sort: "mixed",
    page: "1",
    page_size: "6",
    lang,
  };
}

/**
 * One city's slice of the rail. Projected down to the card's fields in the
 * query function so the SSR payload does not carry every row's full shape,
 * and error-safe for the same reason as the city list above.
 */
export function nearbyRailQueryOptions(citySlug: string, lang: string) {
  const filters = nearbyBookableFilters(citySlug, lang);
  return queryOptions({
    queryKey: [...activityKeys.all, "nearby-rail", citySlug, lang],
    queryFn: async (): Promise<NearbyRailItem[]> => {
      try {
        const response = await activitiesApi.list(filters);
        const rows = (response?.data ?? []) as Array<Record<string, unknown>>;
        return rows
          .filter((row) => row.mode !== "explore")
          .map((row) => {
            const city = (row.city ?? {}) as { name?: string; slug?: string };
            const category = row.category as { name?: string; slug?: string } | null | undefined;
            return {
              id: String(row.id),
              slug: String(row.slug),
              title: String(row.title ?? ""),
              images: Array.isArray(row.images) ? (row.images as string[]) : [],
              price: Number(row.price) || 0,
              typePrice: typeof row.typePrice === "number" ? row.typePrice : undefined,
              rating: typeof row.rating === "number" ? row.rating : undefined,
              mode: typeof row.mode === "string" ? row.mode : undefined,
              segment: typeof row.segment === "string" ? row.segment : null,
              city: { name: city.name ?? citySlug, slug: city.slug ?? citySlug },
              category: category?.slug
                ? { name: category.name ?? category.slug, slug: category.slug }
                : null,
            };
          });
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}
